import React, { useRef, useEffect, useState } from 'react';
import { Group, Rect, Circle, RegularPolygon, Line, Ellipse, Image as KonvaImage } from 'react-konva';
import {
  isGcpDisplayOnlyUrl,
  fetchGcpImageBlobUrl,
  fetchGcpImageDataUrl,
  resolveGcpImageForCanvas,
} from '../api/gcpImagesApi';
import { useEditorStore } from '../store/useEditorStore';

const MIN_SIZE = 8;

function starPoints(radius) {
  const pts = [];
  const outer = radius;
  const inner = radius * 0.4;
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(r * Math.cos(a), r * Math.sin(a));
  }
  return pts;
}

function SelectionOutline({ w, h, isSelected }) {
  if (!isSelected) return null;
  return <Rect width={w} height={h} stroke="#2a318b" strokeWidth={2.5} listening={false} />;
}

/** Non-text scene nodes (shapes, images) with transformer support. */
export default function KonvaSceneObject({ node, isSelected, onLayoutChange }) {
  const groupRef = useRef(null);
  const selectObject = useEditorStore((s) => s.selectObject);
  const updateObjectTransform = useEditorStore((s) => s.updateObjectTransform);
  const updateShapeNode = useEditorStore((s) => s.updateShapeNode);
  const [imageEl, setImageEl] = useState(null);

  const { id, transform: t, type, fabricType, fill, stroke, strokeWidth, strokeDashArray, src, radius, points, closed, rx } = node;
  const objectKey = node.objectKey || node.gcpObjectKey;

  useEffect(() => {
    if (type !== 'image') {
      setImageEl(null);
      return undefined;
    }

    let cancelled = false;
    let revokeUrl = null;

    function loadFromUrl(url, crossOrigin = false) {
      return new Promise((resolve) => {
        const img = new window.Image();
        const urlStr = String(url || '');
        if (crossOrigin) img.crossOrigin = 'anonymous';
        else if (!isGcpDisplayOnlyUrl(urlStr) && /^https?:\/\//i.test(urlStr)) {
          try {
            const u = new URL(urlStr, window.location.href);
            if (u.origin !== window.location.origin) img.crossOrigin = 'anonymous';
          } catch {
            img.crossOrigin = 'anonymous';
          }
        }
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = urlStr;
      });
    }

    async function loadImage() {
      const srcStr = String(src || '').trim();

      // data: URLs (set during PDF export inlining) — load directly, no network needed
      if (srcStr.startsWith('data:')) {
        const img = await loadFromUrl(srcStr);
        if (!cancelled) setImageEl(img || null);
        return;
      }

      // GCP images with objectKey — fetch as same-origin blob URL so the Konva canvas is
      // never tainted by a cross-origin signed GCS URL; untainted canvas is required for
      // stage.toDataURL() during WYSIWYG PDF export.
      if (objectKey) {
        try {
          const blobUrl = await fetchGcpImageBlobUrl(objectKey);
          if (cancelled) return;
          revokeUrl = blobUrl;
          const img = await loadFromUrl(blobUrl);
          if (!cancelled) setImageEl(img || null);
          return;
        } catch {
          try {
            const dataUrl = await fetchGcpImageDataUrl(objectKey);
            if (cancelled) return;
            const img = await loadFromUrl(dataUrl);
            if (!cancelled) setImageEl(img || null);
            return;
          } catch {
            /* fall through to signed-URL / generic fallback below */
          }
        }
      }

      // Fallback: signed GCS URL or other http(s) sources
      let displayUrl = '';
      try {
        displayUrl = await resolveGcpImageForCanvas(srcStr, objectKey);
      } catch {
        displayUrl = srcStr;
      }

      if (!displayUrl) {
        if (!cancelled) setImageEl(null);
        return;
      }

      if (displayUrl.startsWith('blob:')) revokeUrl = displayUrl;

      const img = await loadFromUrl(displayUrl);
      if (!cancelled) setImageEl(img || null);
    }

    loadImage();

    return () => {
      cancelled = true;
      if (revokeUrl && revokeUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(revokeUrl);
        } catch {
          /* ignore */
        }
      }
    };
  }, [type, src, objectKey]);

  const commonGroupProps = {
    ref: groupRef,
    id,
    name: id,
    x: t.x,
    y: t.y,
    rotation: t.rotation || 0,
    opacity: t.opacity ?? 1,
    draggable: !node.locked,
    listening: !node.locked,
    onMouseDown: (e) => {
      e.cancelBubble = true;
    },
    onClick: (e) => {
      if (node.locked) return;
      e.cancelBubble = true;
      const additive = !!(e.evt?.shiftKey || e.evt?.metaKey || e.evt?.ctrlKey);
      selectObject(id, { additive });
    },
    onTap: (e) => {
      if (node.locked) return;
      e.cancelBubble = true;
      selectObject(id);
    },
    onDragEnd: (e) => {
      updateObjectTransform(id, { x: e.target.x(), y: e.target.y() });
      onLayoutChange?.();
    },
    onTransform: (e) => {
      if (type !== 'image' || node.lockAspectRatio === false) return;
      const g = e.target;
      const sx = g.scaleX();
      const sy = g.scaleY();
      if (Math.abs(sx - sy) <= 0.0001) return;
      const uniform = Math.abs(sx) >= Math.abs(sy) ? sx : sy;
      g.scaleX(uniform);
      g.scaleY(uniform);
    },
    onTransformEnd: (e) => {
      const g = e.target;
      let scaleX = g.scaleX();
      let scaleY = g.scaleY();
      if (type === 'image' && node.lockAspectRatio !== false && Math.abs(scaleX - scaleY) > 0.0001) {
        const uniform = Math.abs(scaleX) >= Math.abs(scaleY) ? scaleX : scaleY;
        scaleX = uniform;
        scaleY = uniform;
        g.scaleX(uniform);
        g.scaleY(uniform);
      }
      const newW = Math.max(MIN_SIZE, (t.width || 100) * scaleX);
      const newH = Math.max(MIN_SIZE, (t.height || 40) * scaleY);
      g.scaleX(1);
      g.scaleY(1);
      updateObjectTransform(id, {
        x: g.x(),
        y: g.y(),
        width: newW,
        height: newH,
        rotation: g.rotation(),
      });
      if (type === 'shape') {
        const shapePatch = {};
        if (fabricType === 'circle') {
          shapePatch.radius = Math.min(newW, newH) / 2;
        }
        if (Array.isArray(points) && points.length >= 2 && (scaleX !== 1 || scaleY !== 1)) {
          shapePatch.points = points.map((p) => ({
            x: p.x * scaleX,
            y: p.y * scaleY,
          }));
        }
        if (rx && (scaleX !== 1 || scaleY !== 1)) {
          shapePatch.rx = rx * Math.min(scaleX, scaleY);
        }
        if (Object.keys(shapePatch).length > 0) {
          updateShapeNode(id, shapePatch);
        }
      }
      onLayoutChange?.();
    },
  };

  if (type === 'image') {
    return (
      <Group {...commonGroupProps}>
        {imageEl ? (
          <KonvaImage image={imageEl} width={t.width} height={t.height} listening />
        ) : (
          <Rect
            width={t.width}
            height={t.height}
            fill="rgba(255,255,255,0.001)"
            stroke="#94a3b8"
            strokeWidth={1}
            listening
          />
        )}
        {isSelected ? (
          <Rect
            width={t.width}
            height={t.height}
            stroke="#2a318b"
            strokeWidth={2.5}
            listening={false}
          />
        ) : null}
      </Group>
    );
  }

  if (type !== 'shape') return null;

  const w = t.width || 100;
  const h = t.height || 80;
  const isLine = fabricType === 'line' || fabricType === 'polyline';
  const sw =
    strokeWidth != null && strokeWidth > 0
      ? strokeWidth
      : stroke && stroke !== 'transparent'
        ? isLine
          ? 3
          : 2
        : 0;
  const dash = strokeDashArray;
  const shapeFill = fill ?? (isLine ? undefined : '#e2e8f0');
  const shapeStroke = stroke ?? '#94a3b8';

  if (fabricType === 'circle') {
    const r = Math.min(w, h) / 2;
    return (
      <Group {...commonGroupProps}>
        <Circle x={r} y={r} radius={r} fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} listening />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  if (fabricType === 'ellipse') {
    return (
      <Group {...commonGroupProps}>
        <Ellipse x={w / 2} y={h / 2} radiusX={w / 2} radiusY={h / 2} fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} listening />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  if (fabricType === 'triangle') {
    return (
      <Group {...commonGroupProps}>
        <RegularPolygon x={w / 2} y={h / 2} sides={3} radius={Math.min(w, h) / 2} fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} listening />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  if (fabricType === 'polygon' && Array.isArray(points) && points.length >= 3) {
    const flat = points.flatMap((p) => [p.x, p.y]);
    return (
      <Group {...commonGroupProps}>
        <Line points={flat} closed fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} listening />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  if (fabricType === 'line' || fabricType === 'polyline') {
    const flat =
      Array.isArray(points) && points.length >= 2
        ? points.flatMap((p) => [p.x, p.y])
        : [0, h / 2, w, h / 2];
    const openPolyline = fabricType === 'polyline' && closed === false;
    return (
      <Group {...commonGroupProps}>
        <Line
          points={flat}
          closed={openPolyline ? false : fabricType === 'polyline'}
          fill={openPolyline ? undefined : shapeFill}
          stroke={shapeStroke}
          strokeWidth={sw || 3}
          dash={dash}
          lineCap="round"
          lineJoin="round"
          listening
        />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  if (fabricType === 'polygon' && (!points || points.length < 3)) {
    return (
      <Group {...commonGroupProps}>
        <Line points={starPoints(Math.min(w, h) / 2)} closed fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} x={w / 2} y={h / 2} listening />
        <SelectionOutline w={w} h={h} isSelected={isSelected} />
      </Group>
    );
  }

  return (
    <Group {...commonGroupProps}>
      <Rect width={w} height={h} fill={shapeFill} stroke={shapeStroke} strokeWidth={sw} dash={dash} cornerRadius={rx || 0} listening />
      <SelectionOutline w={w} h={h} isSelected={isSelected} />
    </Group>
  );
}
